/**
 * Utilities for building typed query parameters from Cloudscape filter components.
 */
import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { rangeStartEnd } from '../filtering/filters';

export type ListQueryParams = {
  page?: number;
  limit?: number;
  op?: string;
  o?: string[];
  k?: string[];
  v?: string[];
  sort_by?: string | null;
  sort_direction?: string | null;
  text_content?: string | null;
};

export function buildListQueryParams(opts: {
  query?: PropertyFilterQuery;
  page?: number;
  limit?: number;
  range?: DateRangePickerProps.Value | null;
  now?: Date;
  rangeName?: string;
  sortingField?: string;
  sortDirection?: 'asc' | 'desc';
  textQuery?: string;
}): ListQueryParams {
  const {
    query,
    page = 1,
    limit = 20,
    range,
    now = new Date(),
    rangeName,
    sortingField,
    sortDirection,
    textQuery,
  } = opts;

  const operators = query ? query.tokens.map((token) => token.operator) : [];
  const propertyKeys = query ? query.tokens.map((token) => token.propertyKey as string) : [];
  const values = query ? query.tokens.map((token) => token.value as string) : [];

  if (range) {
    const { start, end } = rangeStartEnd(range, now);
    let rangePropertyKey = 'created_at';
    if (rangeName) {
      rangePropertyKey = rangeName;
    }
    propertyKeys.push(rangePropertyKey);
    operators.push('>=');
    values.push(start.toISOString());
    propertyKeys.push(rangePropertyKey);
    operators.push('<=');
    values.push(end.toISOString());
  }

  const op = query ? (query.operation === 'or' ? 'or' : undefined) : undefined;

  return {
    page,
    limit,
    op,
    o: operators.length > 0 ? operators : undefined,
    k: propertyKeys.length > 0 ? propertyKeys : undefined,
    v: values.length > 0 ? values : undefined,
    sort_by: sortingField ?? null,
    sort_direction: sortDirection ?? null,
    text_content: textQuery?.trim() ?? null,
  };
}

export function withHiddenFilter(query: PropertyFilterQuery): PropertyFilterQuery {
  if (query.tokens.some((token) => token.propertyKey === 'hidden')) {
    return query;
  }
  return {
    operation: query.operation,
    tokens: [
      ...query.tokens,
      { propertyKey: 'hidden', operator: '=', value: 'none' },
    ],
  };
}

export function withDisabledFilter(query: PropertyFilterQuery): PropertyFilterQuery {
  if (query.tokens.some((token) => token.propertyKey === 'disabled')) {
    return query;
  }
  return {
    operation: query.operation,
    tokens: [
      ...query.tokens,
      { propertyKey: 'disabled', operator: '=', value: 'none' },
    ],
  };
}
